import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Box, Typography, Paper, useTheme, IconButton } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useSelector } from 'react-redux';
import { calculateRequiredMargin } from '../../../services/backendAPI';
import { calcMargin } from '../../../hooks/useOrderFormCore';
import { extractSymbolForMargin } from '../../../hooks/useOrderFormUtils';
import useBalance from '../../Portfolio/Positions/useBalance';
import { styles } from './BasketDetailsCSS';

const DEBOUNCE_MS = 2000; // 2-second heartbeat for server-side

const BasketMarginSummary = ({ instruments, ltpMapRef, onTickCallbackRef, marginsCacheRef }) => {
  const theme = useTheme();
  const [totalMargin, setTotalMargin] = useState(0);
  const [loading, setLoading] = useState(false);
  
  const balanceData = useBalance();
  const masterSettings = useSelector(state => state.profile.masterSettings) || {};
  const userSettings = useSelector((state) => state.profile.userSettings || {});
  const PRIME_SETTINGS = userSettings.order_margin;
  const marginConfig = useSelector((state) => state.margin.marginConfig);

  const isServerSide = useCallback((exchange) => {
    const val = masterSettings?.scrip_allow_margin_config?.[exchange];
    return val === true || val === 'true';
  }, [masterSettings]);

  const previousMarginsRef = useRef({});
  // Tracks, per instrument id, whether the cached margin came from a local calcMargin
  // fallback (true) rather than a genuine API success (false/absent) — see fetchServerMargin.
  const apiFailedRef = useRef({});
  const heartbeatTimerRef = useRef(null);
  // Prevents overlapping fetch cycles (heartbeat firing again before a slow/failing prior
  // call finishes), which could otherwise let an older, stale cycle overwrite fresher results.
  const isFetchingRef = useRef(false);

  // Helper to extract the price we should use for margin
  const getEffectivePrice = (inst) => {
    const isMarket = inst.order_type === 'MARKET' || inst.order_type === 'STOPLOSSMARKET';
    let effectivePrice = Number(inst.price) || 0;

    if (isMarket && ltpMapRef.current[inst.id]) {
      const { ltp, bestBid, bestAsk } = ltpMapRef.current[inst.id];
      const marketPrice = inst.order_side === 'BUY' ? bestAsk : bestBid;
      effectivePrice = Number(marketPrice) || Number(ltp) || 0;
    }
    return effectivePrice;
  };

  // Local calcMargin computation for a single instrument — used both for instruments
  // configured as client-side, and as a silent fallback when the server-side API fails.
  const computeLocalMarginForInstrument = (inst) => {
    const effectivePrice = getEffectivePrice(inst);
    const symbol = extractSymbolForMargin(inst.exchange, inst.trading_symbol, inst);

    return calcMargin({
      order_config: PRIME_SETTINGS,
      exchange: inst.exchange,
      orderSide: inst.order_side,
      product: inst.product,
      price: effectivePrice,
      qty: Number(inst.quantity) || 0,
      instrumentName: inst.series,
      raw_board_lot_quantity: inst.board_lot_quantity,
      marginConfig: marginConfig,
      symbol: symbol,
      instrumentDetails: inst
    });
  };

  // The actual server API call logic
  const fetchServerMargin = async () => {
    if (!instruments || instruments.length === 0) {
      setTotalMargin(0);
      return;
    }
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    setLoading(true);
    let calculatedTotal = 0;
    let anyApiFired = false;

    // Simulate sending all to API (Current implementation maps each to API call)
    // In a real optimized endpoint, you'd send an array. Our current backend takes them individually.
    const marginPromises = instruments.map(async (inst) => {
      if (!isServerSide(inst.exchange)) return null;
      const effectivePrice = getEffectivePrice(inst);
      
      // Prevent API failure by skipping calculation if price is 0 (e.g. before first WebSocket tick)
      if (effectivePrice <= 0) {
        return null;
      }
      
      // PERMANENT CACHE HIT: only skip re-fetching when the cached value came from a genuine
      // API success — a local-fallback value (apiFailedRef) keeps retrying every heartbeat.
      if (previousMarginsRef.current[inst.id] !== undefined && !apiFailedRef.current[inst.id]) {
        return previousMarginsRef.current[inst.id];
      }

      anyApiFired = true;

      const payload = {
        exchange: inst.exchange,
        instrument_type: inst.series || "",
        product_type: inst.product,
        side: inst.order_side,
        underlying_token: Number(inst.underlying_token) || 0,
        token: Number(inst.instrument_token),
        trading_symbol: inst.trading_symbol,
        quantity: Number(inst.quantity) || 0,
        price: String(effectivePrice),
        lot_size: Number(inst.board_lot_quantity) || 1,
      };

      try {
        const response = await calculateRequiredMargin(payload);
        if (response && response.status === "success" && response.data) {
          apiFailedRef.current[inst.id] = false;
          return Number(response.data.required_margin) || 0;
        }
        console.error("Margin API error: non-success response", response);
      } catch (err) {
        console.error("Margin API error:", err);
      }
      // Silent fallback: server-side margin API failed for this instrument, use local calcMargin
      // instead (still gated behind isServerSide above) while keeping retries alive next heartbeat.
      apiFailedRef.current[inst.id] = true;
      try {
        return computeLocalMarginForInstrument(inst);
      } catch (calcErr) {
        // A single instrument's config gap (e.g. missing PRIME_SETTINGS entry) must never
        // take down margin display for the rest of the basket — keep last known value if any.
        console.error("calcMargin fallback failed for instrument:", inst.id, calcErr);
        return previousMarginsRef.current[inst.id] ?? 0;
      }
    });

    try {
      const results = await Promise.all(marginPromises);

      const marginsObj = {};
      instruments.forEach((inst, i) => {
        // If it was a valid fetch (not null), save it permanently
        if (results[i] !== null) {
            previousMarginsRef.current[inst.id] = results[i];
        }
        
        // We use the cached value if available to prevent flashing 0
        const finalMarginForInst = previousMarginsRef.current[inst.id] !== undefined 
            ? previousMarginsRef.current[inst.id] 
            : (results[i] || 0);
            
        marginsObj[inst.id] = finalMarginForInst;
        if (marginsCacheRef && marginsCacheRef.current) {
          marginsCacheRef.current[inst.id] = finalMarginForInst;
        }
      });
      
      // Only dispatch the event if an actual API call was fired to prevent unnecessary row re-renders
      if (anyApiFired) {
        window.dispatchEvent(new CustomEvent('BASKET_MARGINS_UPDATED', { detail: marginsObj }));
      }

      calculatedTotal = instruments.reduce((sum, inst) => {
          return sum + (previousMarginsRef.current[inst.id] || 0);
      }, 0);
      
      setTotalMargin(calculatedTotal);
    } catch (err) {
      // Defense in depth — every per-instrument promise above already catches its own
      // errors, so this should be unreachable, but must never leave isFetchingRef stuck.
      console.error("Unexpected error while aggregating basket margins:", err);
    } finally {
      isFetchingRef.current = false;
      setLoading(false);
    }
  };

  // Client-Side (Instant) Logic
  const calculateLocalMargin = () => {
    if (!instruments || instruments.length === 0) {
      setTotalMargin(0);
      return;
    }

    const marginsObj = {};
    let calculatedTotal = 0;
    instruments.forEach((inst) => {
      if (isServerSide(inst.exchange)) return;
      let margin;
      try {
        margin = computeLocalMarginForInstrument(inst);
      } catch (err) {
        // One instrument's config gap must not stop the rest of the basket from updating.
        console.error("calcMargin failed for instrument:", inst.id, err);
        margin = previousMarginsRef.current[inst.id] ?? 0;
      }

      marginsObj[inst.id] = margin;
      previousMarginsRef.current[inst.id] = margin;
      if (marginsCacheRef && marginsCacheRef.current) {
        marginsCacheRef.current[inst.id] = margin;
      }
      calculatedTotal += margin;
    });
    
    // Dispatch event so individual rows can update their margin silently
    window.dispatchEvent(new CustomEvent('BASKET_MARGINS_UPDATED', { detail: marginsObj }));
    
    const total = instruments.reduce((sum, inst) => sum + (previousMarginsRef.current[inst.id] || 0), 0);
    setTotalMargin(total);
  };

  const basketSignature = useMemo(() => {
    if (!instruments || instruments.length === 0) return '';
    return instruments.map(inst => `${inst.id}-${inst.quantity}-${inst.price}-${inst.order_type}`).join('|');
  }, [instruments]);

  const anyServerSide = useMemo(() => instruments.some(inst => isServerSide(inst.exchange)), [instruments, isServerSide]);
  const anyLocal = useMemo(() => instruments.some(inst => !isServerSide(inst.exchange)), [instruments, isServerSide]);

  const handleManualRefresh = () => {
    if (anyServerSide && !loading) {
      // Clear the permanent cache to force a fresh fetch for all instruments
      previousMarginsRef.current = {};
      apiFailedRef.current = {};
      fetchServerMargin();
    }
  };

  // Set up the listeners and intervals when instruments array changes
  useEffect(() => {
    if (!instruments || instruments.length === 0) {
      setTotalMargin(0);
      return;
    }

    // Reset memory when instruments change (e.g. user added/deleted a row)
    previousMarginsRef.current = {};
    apiFailedRef.current = {};

    if (anyServerSide) {
      // 1. Fetch immediately on load
      fetchServerMargin();
      
      // 2. Set up the 2-second heartbeat
      if (heartbeatTimerRef.current) clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = setInterval(() => {
        fetchServerMargin();
      }, DEBOUNCE_MS);
    } else {
      // 2. Clear any server-side heartbeat
      if (heartbeatTimerRef.current) {
        clearInterval(heartbeatTimerRef.current);
        heartbeatTimerRef.current = null;
      }
    }

    if (anyLocal) {
      // 1. Calculate immediately on load
      calculateLocalMargin();
      
      // 3. Subscribe to the instant tick event from the parent
      if (onTickCallbackRef) {
        onTickCallbackRef.current = () => {
          calculateLocalMargin();
        };
      }
    } else {
      if (onTickCallbackRef) {
        onTickCallbackRef.current = null;
      }
    }

    return () => {
      if (heartbeatTimerRef.current) clearInterval(heartbeatTimerRef.current);
      if (onTickCallbackRef) onTickCallbackRef.current = null;
    };
  }, [basketSignature, anyServerSide, anyLocal]); // Only re-run if the structure of the basket changes, or the toggle changes

  if (!instruments || instruments.length === 0) return null;

  return (
    <Paper 
      elevation={0}
      sx={styles.marginSummaryPaper(theme)}
    >
      <Box sx={styles.marginSummaryBoxLeft}>
        <Typography variant="caption" color="text.secondary" sx={styles.marginSummaryLabel}>
          Total required margin
        </Typography>
        <Typography variant="subtitle2" fontWeight={700} sx={{ ...styles.marginSummaryValue, display: 'flex', alignItems: 'center' }}>
          {loading ? 'Calculating...' : `₹${totalMargin.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          {anyServerSide && (
            <IconButton 
              size="small" 
              color="primary"
              onClick={handleManualRefresh}
              disabled={loading}
              sx={{ ml: 0.5, p: 0.25 }}
              title="Refresh required margin"
            >
              <RefreshIcon fontSize="inherit" sx={{ fontSize: '1.1rem' }} />
            </IconButton>
          )}
        </Typography>
      </Box>
      <Box sx={styles.marginSummaryBoxRight}>
        <Typography variant="caption" color="text.secondary" sx={styles.marginSummaryLabel}>
          Available margin
        </Typography>
        <Typography variant="subtitle2" fontWeight={700} sx={styles.marginSummaryValue}>
          {balanceData.isLoading ? '--' : `₹${balanceData.availableCash.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
        </Typography>
      </Box>
    </Paper>
  );
};

export default React.memo(BasketMarginSummary);
